import { ReactNode, useState } from 'react'
import { Avatar, Dropdown, Typography, theme } from 'antd'
import { CheckOutlined, EditOutlined, DeleteOutlined, EnterOutlined, SmileOutlined } from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../Stores'
import { Message } from '../../Contracts'
import { formatMessageTime } from '../../Helpers/formatDate'
import { truncateText } from '../../Helpers/truncateText'
import { stripHtml } from '../../Helpers/stripHtml'
import { CHAT_DELIVERY_ENABLED, CHAT_REACTIONS_ENABLED } from '../../Helpers/chatFeatures'
import ReactionPicker from './ReactionPicker'
import MessageReactions from './MessageReactions'

const { Text } = Typography

const AVATAR_SIZE = 36
const AVATAR_GAP = 8

interface MessageWrapperProps {
  message: Message
  children: ReactNode
  isFirstInGroup: boolean
  isLastInGroup: boolean
}

const MessageWrapper = observer(
  ({ message, children, isFirstInGroup, isLastInGroup }: MessageWrapperProps) => {
    const { userStore, messageStore, uiStore, chatStore } = useStore()
    const { token } = theme.useToken()
    const [isHovered, setIsHovered] = useState(false)

    const isMine = message.senderId === userStore.currentUserId
    const sender = userStore.getById(message.senderId)

    const bubbleBg = isMine ? token.colorPrimaryBg : token.colorBgContainer
    const bubbleBorder = isMine ? token.colorPrimaryBorder : token.colorBorderSecondary
    const activeEmojis = message.reactions
      ?.filter(r => r.userIds.includes(userStore.currentUserId))
      .map(r => r.emoji) ?? []

    // Delivery ✓/✓✓ (Telegram-style, no "delivered" middle state): computed
    // from how far the OTHER participants have read, not stored per message —
    // one read pointer per user flips every message at once, and mobx
    // re-renders the observer when the pointer moves
    const renderStatus = () => {
      if (!isMine || !CHAT_DELIVERY_ENABLED || message.isDeleted) return null

      const chat = chatStore.chats.find((c) => c.id === message.chatId)
      const isRead = !!chat && chatStore.othersMaxReadAt(chat) >= message.timestamp

      if (!isRead) {
        return <CheckOutlined style={{ fontSize: 12, marginLeft: 4, color: token.colorTextQuaternary }} />
      }
      return (
        <span style={{ position: 'relative', display: 'inline-block', width: 16, height: 12, marginLeft: 4 }}>
          <CheckOutlined style={{ position: 'absolute', left: 0, top: 0, fontSize: 12, color: token.colorPrimary }} />
          <CheckOutlined style={{ position: 'absolute', left: 4, top: 0, fontSize: 12, color: token.colorPrimary }} />
        </span>
      )
    }

    const menuItems: MenuProps['items'] = [
      {
        key: 'reply',
        label: 'Відповісти',
        icon: <EnterOutlined />,
      },
      // Редагування і видалення — ТІЛЬКИ своїх (дзеркало ChatRule:
      // MESSAGE_UPDATE/MESSAGE_DELETE = isAuthor; чужі бек відбиває 403,
      // тож показувати мертві пункти — брехати юзеру)
      ...(isMine
        ? [
            {
              key: 'edit',
              label: 'Редагувати',
              icon: <EditOutlined />,
            },
            {
              key: 'delete',
              label: 'Видалити',
              icon: <DeleteOutlined />,
              danger: true,
            },
          ]
        : []),
    ]

    const scrollToMessage = (messageId: string) => {
      const el = document.getElementById(`message-${messageId}`)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el.classList.add('message-highlight')
      setTimeout(() => el.classList.remove('message-highlight'), 1500)
    }

    const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
      if (key === 'delete') {
        messageStore.deleteMessage(message.id)
      } else if (key === 'reply') {
        uiStore.setReplyToMessage(message.id)
      } else if (key === 'edit') {
        uiStore.setEditingMessage(message.id)
      }
    }

    if (message.isDeleted) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: isMine ? 'flex-end' : 'flex-start',
            padding: '2px 16px',
            paddingLeft: isMine ? 16 : 16 + AVATAR_SIZE + AVATAR_GAP,
            paddingRight: isMine ? 16 + AVATAR_SIZE + AVATAR_GAP : 16,
          }}
        >
          <Text type="secondary" italic style={{ fontSize: 13 }}>
            Повідомлення видалено
          </Text>
        </div>
      )
    }

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: isMine ? 'row-reverse' : 'row',
          alignItems: 'flex-end',
          padding: `${isFirstInGroup ? 6 : 1}px 16px 1px 16px`,
          gap: AVATAR_GAP,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Avatar column — fixed width, avatar only on last message in group */}
        <div style={{ width: AVATAR_SIZE, flexShrink: 0 }}>
          {isLastInGroup && (
            <Avatar src={sender?.avatarUrl} size={AVATAR_SIZE}>
              {sender?.firstName.charAt(0) ?? '?'}
            </Avatar>
          )}
        </div>

        {/* Bubble */}
        <Dropdown
          menu={{ items: menuItems, onClick: handleMenuClick }}
          trigger={['contextMenu']}
        >
          <div
            style={{
              maxWidth: '70%',
              position: 'relative',
            }}
          >
            <div
              style={{
                background: bubbleBg,
                border: `1px solid ${bubbleBorder}`,
                borderRadius: isLastInGroup
                  ? isMine
                    ? '12px 12px 0 12px'
                    : '12px 12px 12px 0'
                  : 12,
                padding: '8px 12px',
                position: 'relative',
              }}
            >
              {/* Sender name inside bubble — only for first message in group */}
              {isFirstInGroup && (
                <Text
                  strong
                  style={{
                    fontSize: 12,
                    color: token.colorPrimary,
                    display: 'block',
                    marginBottom: 2,
                  }}
                >
                  {sender ? `${sender.firstName} ${sender.lastName}` : 'Невідомий'}
                </Text>
              )}

              {/* Reply quote */}
              {message.replyToId && (() => {
                const replied = messageStore.getMessageById(message.replyToId!)
                if (!replied) return null
                const repliedSender = userStore.getById(replied.senderId)
                const firstFile = replied.files?.[0]
                const preview = replied.isDeleted
                  ? 'Повідомлення видалено'
                  : firstFile
                  ? firstFile.type === 'photo' ? '📷 Фото'
                  : firstFile.type === 'video' ? '📹 Відео'
                  : `📎 ${firstFile.name}`
                  : truncateText(stripHtml(replied.content), 60)
                return (
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      scrollToMessage(message.replyToId!)
                    }}
                    style={{
                      borderLeft: `3px solid ${token.colorPrimary}`,
                      paddingLeft: 8,
                      marginBottom: 6,
                      cursor: 'pointer',
                      background: token.colorFillQuaternary,
                      borderRadius: '0 6px 6px 0',
                      padding: '4px 8px 4px 8px',
                    }}
                  >
                    <Text strong style={{ fontSize: 12, color: token.colorPrimary, display: 'block' }}>
                      {repliedSender ? `${repliedSender.firstName} ${repliedSender.lastName}` : 'Невідомий'}
                    </Text>
                    <Text type="secondary" italic={replied.isDeleted} style={{ fontSize: 12, display: 'block' }}>
                      {preview}
                    </Text>
                  </div>
                )
              })()}

              {children}

              {/* Reactions + time row */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  gap: 4,
                  marginTop: 4,
                }}
              >
                {message.reactions && message.reactions.length > 0 && (
                  <MessageReactions
                    reactions={message.reactions}
                    messageId={message.id}
                  />
                )}
                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexShrink: 0,
                  }}
                >
                  {message.isEdited && (
                    <Text type="secondary" style={{ fontSize: 10, marginRight: 4 }}>
                      ред.
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {formatMessageTime(message.timestamp)}
                  </Text>
                  {renderStatus()}
                </div>
              </div>

              {/* Bubble tail — on last message in group */}
              {isLastInGroup && (
                <svg
                  width="12"
                  height="19"
                  viewBox={isMine ? '0 -0.5 12 19' : '0 -0.5 12 19'}
                  style={{
                    position: 'absolute',
                    bottom: -1,
                    [isMine ? 'right' : 'left']: -12,
                    display: 'block',
                    transform: isMine ? 'scaleX(-1)' : undefined,
                  }}
                >
                  <path
                    d="M12 0V18H0C6.63 18 12 11.37 12 6V0Z"
                    fill={bubbleBg}
                  />
                  <path
                    d="M12 18H0C6.63 18 12 11.37 12 6"
                    stroke={bubbleBorder}
                    strokeWidth="1"
                    fill="none"
                  />
                </svg>
              )}
            </div>

            {/* Hover action dots + reaction button */}
            {isHovered && (
              <>
                <Dropdown
                  menu={{ items: menuItems, onClick: handleMenuClick }}
                  trigger={['click']}
                  placement={isMine ? 'bottomLeft' : 'bottomRight'}
                >
                  <div
                    className="chat-hover-actions"
                    style={{
                      position: 'absolute',
                      top: 4,
                      [isMine ? 'left' : 'right']: -28,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: token.colorBgElevated,
                      border: `1px solid ${token.colorBorderSecondary}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: token.colorTextSecondary,
                      boxShadow: token.boxShadowTertiary,
                    }}
                  >
                    •••
                  </div>
                </Dropdown>
                {CHAT_REACTIONS_ENABLED && (
                <div
                  className="chat-hover-actions"
                  style={{
                    position: 'absolute',
                    bottom: 4,
                    [isMine ? 'left' : 'right']: -28,
                  }}
                >
                  <ReactionPicker
                    onSelect={(emoji) => messageStore.toggleReaction(message.id, emoji)}
                    activeEmojis={activeEmojis}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        background: token.colorBgElevated,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: token.boxShadowTertiary,
                      }}
                    >
                      <SmileOutlined style={{ fontSize: 12, color: token.colorTextSecondary }} />
                    </div>
                  </ReactionPicker>
                </div>
                )}
              </>
            )}

          </div>
        </Dropdown>
      </div>
    )
  },
)

export default MessageWrapper
